import { LuSearch, LuX, LuPlus, LuSettings } from 'react-icons/lu';
import { ml } from 'src/_shared/services/utils.js';
import { lightenHex, accentRgba, darkenHex } from './utils.js';
import { getTagHex } from './constants.js';
import SortDropdown from './SortDropdown.jsx';

function SearchFilterBar({
	searchQuery,
	onSearchChange,
	allTags,
	selectedTags,
	onTagToggle,
	onClearTags,
	sortBy,
	onSortChange,
	isModerator,
	onAddNew,
	onOpenSettings,
	accentColor,
	totalResults,
	currentUser,
	allMultiLingualData,
}) {
	return (
		<div className="mb-8" style={{ animation: 'fadeIn 0.3s ease-out both', position: 'relative', zIndex: 10 }}>
			<div className="flex flex-col gap-3 sm:flex-row">
				<div className="relative" style={{ flex: 1 }}>
					<LuSearch
						style={{
							position: 'absolute',
							left: 14,
							top: '50%',
							transform: 'translateY(-50%)',
							fontSize: 14,
							color: '#475569',
							zIndex: 1,
						}}
					/>
					<input
						type="text"
						placeholder={ml(
							'Search announcements...',
							currentUser,
							allMultiLingualData
						)}
						value={searchQuery}
						onChange={(e) => onSearchChange(e.target.value)}
						className="w-full rounded-xl text-sm"
						style={{
							paddingLeft: 40,
							paddingRight: 16,
							paddingTop: 10,
							paddingBottom: 10,
							background: '#fff',
							border: '1px solid #e2e8f0',
							outline: 'none',
							color: '#334155',
							transition: 'all 0.2s',
						}}
						onFocus={(e) => {
							e.target.style.borderColor = lightenHex(accentColor);
							e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
						}}
						onBlur={(e) => {
							e.target.style.borderColor = '#e2e8f0';
							e.target.style.boxShadow = 'none';
						}}
					/>
					{searchQuery && (
						<button
							type="button"
							onClick={() => onSearchChange('')}
							className="absolute rounded-full"
							style={{
								right: 12,
								top: '50%',
								transform: 'translateY(-50%)',
								padding: 2,
								background: 'none',
								border: 'none',
								color: '#475569',
								cursor: 'pointer',
							}}
						>
							<LuX size={11} />
						</button>
					)}
				</div>

				<div className="flex gap-2">
					<SortDropdown
						sortBy={sortBy}
						onSortChange={onSortChange}
						currentUser={currentUser}
						allMultiLingualData={allMultiLingualData}
						accentColor={accentColor}
					/>

					{isModerator && (
						<>
							<button
								type="button"
								onClick={onAddNew}
								className="flex items-center gap-2 text-white"
								style={{
									paddingLeft: 16,
									paddingRight: 16,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									background: accentColor,
									border: 'none',
									cursor: 'pointer',
									boxShadow: `0 1px 3px ${accentRgba(accentColor, 0.3)}`,
									whiteSpace: 'nowrap',
									transition: 'all 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = darkenHex(accentColor);
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = accentColor;
								}}
							>
								<LuPlus size={14} />
								<span className="hidden sm:inline">
									{ml('New Announcement', currentUser, allMultiLingualData)}
								</span>
							</button>
							<button
								type="button"
								onClick={onOpenSettings}
								style={{
									width: 40,
									height: 40,
									borderRadius: '0.75rem',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: 0,
									lineHeight: 0,
									background: '#fff',
									border: '1px solid #e2e8f0',
									color: '#475569',
									cursor: 'pointer',
									transition: 'all 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.color = '#475569';
									e.currentTarget.style.borderColor = '#cbd5e1';
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.color = '#475569';
									e.currentTarget.style.borderColor = '#e2e8f0';
								}}
							>
								<LuSettings size={16} />
							</button>
						</>
					)}
				</div>
			</div>

			{allTags.length > 0 && (
				<div className="mt-4 flex flex-wrap items-center gap-2">
					<span
						className="mr-1 text-xs font-semibold uppercase"
						style={{ color: '#475569', letterSpacing: '0.05em', fontSize: 11 }}
					>
						{ml('Filter', currentUser, allMultiLingualData)}:
					</span>
					{allTags.map((tag) => {
						const isSelected = selectedTags.includes(tag);
						return (
							<button
								key={tag}
								type="button"
								onClick={() => onTagToggle(tag)}
								style={{
									paddingLeft: 10,
									paddingRight: 10,
									paddingTop: 6,
									paddingBottom: 6,
									borderRadius: 8,
									border: '1px solid',
									borderColor: isSelected
										? accentRgba(getTagHex(tag), 0.3)
										: '#e2e8f0',
									background: isSelected ? accentRgba(getTagHex(tag), 0.1) : '#fff',
									color: isSelected ? getTagHex(tag) : '#475569',
									cursor: 'pointer',
									transition: 'all 0.2s',
									fontSize: 11,
									lineHeight: 1,
									display: 'inline-flex',
									alignItems: 'center',
									opacity: isSelected ? 0.9 : 1,
								}}
							>
								{tag}
							</button>
						);
					})}
					{selectedTags.length > 0 && (
						<button
							type="button"
							onClick={onClearTags}
							style={{
								paddingLeft: 10,
								paddingRight: 10,
								paddingTop: 6,
								paddingBottom: 6,
								display: 'inline-flex',
								alignItems: 'center',
								gap: 4,
								borderRadius: 8,
								fontSize: 11,
								fontWeight: 500,
								lineHeight: 1,
								color: '#475569',
								background: 'none',
								border: '1px solid transparent',
								cursor: 'pointer',
								transition: 'color 0.2s',
							}}
							onMouseOver={(e) => {
								e.currentTarget.style.color = '#ef4444';
							}}
							onMouseOut={(e) => {
								e.currentTarget.style.color = '#475569';
							}}
						>
							<LuX size={10} />
							{ml('Clear', currentUser, allMultiLingualData)}
						</button>
					)}
				</div>
			)}

			<p className="mt-4 text-xs" style={{ color: '#475569' }}>
				{totalResults}{' '}
				{ml(
					totalResults !== 1 ? 'announcements' : 'announcement',
					currentUser,
					allMultiLingualData
				)}
				{selectedTags.length > 0 || searchQuery
					? ` ${ml('found', currentUser, allMultiLingualData)}`
					: ''}
			</p>
		</div>
	);
}

export default SearchFilterBar;
